import { useState } from 'react';
import { Search, Loader } from 'lucide-react';
import api from '../../services/api';

interface ImageResult {
  id: string;
  source: 'arasaac' | 'pixabay';
  previewUrl: string;
  downloadUrl: string;
  label: string;
}

interface ImageSearchProps {
  onSelect: (url: string, label: string) => void;
}

type Source = 'arasaac' | 'pixabay' | 'all';

export default function ImageSearch({ onSelect }: ImageSearchProps) {
  const [query, setQuery] = useState('');
  const [source, setSource] = useState<Source>('arasaac');
  const [results, setResults] = useState<ImageResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get('/rehab/images', { params: { q: query, source } });
      if (res.data.ok) setResults(res.data.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Source selector */}
      <div className="flex gap-2">
        {(['arasaac', 'pixabay', 'all'] as Source[]).map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setSource(s)}
            className={`px-3 py-1 text-sm rounded-full border transition-colors ${
              source === s
                ? 'border-neureka-yellow bg-neureka-yellow/20 text-neureka-yellow'
                : 'border-white/20 text-white/60 hover:border-white/40'
            }`}
          >
            {s === 'all' ? 'Todos' : s === 'arasaac' ? 'ARASAAC' : 'Pixabay'}
          </button>
        ))}
      </div>

      {/* Search input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Buscar imagen (ej: perro, casa, manzana...)"
          className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-neureka-yellow"
        />
        <button
          type="button"
          onClick={search}
          disabled={loading}
          className="px-4 py-2 bg-neureka-yellow text-neureka-dark rounded-lg hover:bg-yellow-400 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>

      {/* Results */}
      {loading && (
        <div className="text-center py-4 text-white/60">Buscando...</div>
      )}

      {!loading && searched && results.length === 0 && (
        <div className="text-center py-4 text-white/60">Sin resultados</div>
      )}

      {!loading && results.length > 0 && (
        <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
          {results.map(img => (
            <button
              key={`${img.source}-${img.id}`}
              type="button"
              onClick={() => onSelect(img.downloadUrl, img.label)}
              className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-neureka-yellow transition-colors bg-white/5"
              title={img.label}
            >
              <img
                src={img.previewUrl}
                alt={img.label}
                className="w-full h-full object-contain p-1"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
